import { RadioButton } from '@shinetools/lumen-react'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useBalanceConfig, useBalanceLabels } from '@features/balanceLedger'
import { BalanceFilterType } from '@features/balanceLedger/enums/balanceFilterType'

import { useSuppliersBalanceFiltersContext } from '../../../../contexts/suppliersBalanceFiltersContext'
import { SuppliersBalanceFilterType } from '../../../../enums/suppliersBalanceFilterType'
import { BalanceDirection } from '../../../../types/suppliersBalanceFilters'
import { FilterChipShell } from '../../FilterChipShell'
import { DirectionList, DirectionOptionRow } from './styles'

const ALL_VALUE = 'all'

type DirectionOption = typeof ALL_VALUE | BalanceDirection

const OPTIONS: DirectionOption[] = [ALL_VALUE, 'positive', 'negative']

// Static translation keys per option — avoids building `bt()` key paths with dynamic interpolation.
const DIRECTION_LABEL_KEY: Record<DirectionOption, string> = {
  [ALL_VALUE]: 'filters.balance_direction.value.all',
  positive: 'filters.balance_direction.value.positive',
  negative: 'filters.balance_direction.value.negative',
}

const TYPE = SuppliersBalanceFilterType.BalanceDirection

export const BalanceDirectionFilterChip = (): ReactElement => {
  const { filterTypeMeta } = useBalanceConfig()
  const { bt } = useBalanceLabels()
  const { t } = useTranslation()
  const { filters, setFilterValue, removeFilter, resetFilter } = useSuppliersBalanceFiltersContext()
  const current = filters.balanceDirection
  const selected: DirectionOption = current ?? ALL_VALUE
  const meta = filterTypeMeta[BalanceFilterType.BalanceDirection]

  const selectOption = (option: DirectionOption) => {
    const value = option === ALL_VALUE ? undefined : option
    setFilterValue(TYPE, value, value ? bt(DIRECTION_LABEL_KEY[value]) : undefined)
  }

  const handleOptionSelect = (option: DirectionOption) => () => selectOption(option)

  const previewKey = current ? DIRECTION_LABEL_KEY[current] : undefined

  return (
    <FilterChipShell
      type={TYPE}
      icon={meta.icon}
      label={t(meta.labelKey)}
      valuePreview={previewKey ? bt(previewKey) : undefined}
      isActive={current !== undefined}
      onDelete={() => removeFilter(TYPE)}
      onReset={() => resetFilter(TYPE)}
    >
      <DirectionList role="radiogroup" aria-label={t(meta.labelKey)}>
        {OPTIONS.map((option) => (
          <DirectionOptionRow key={option} onClick={handleOptionSelect(option)}>
            <RadioButton
              checked={selected === option}
              comfortArea={false}
              label={bt(DIRECTION_LABEL_KEY[option])}
              name={TYPE}
              onChange={handleOptionSelect(option)}
              value={option}
            />
          </DirectionOptionRow>
        ))}
      </DirectionList>
    </FilterChipShell>
  )
}
